// ProcessMotorDialog — Container som samlar Run-historik, API-keys,
// Webhooks, Schedules i en dialog. Entry-point för Enterprise-users
// att konfigurera workflow:et som process.
//
// Öppnas från canvas-toolbar-knapp ("Process-motor") som visas när
// usePlan().limits.canRunProcesses && activeWorkflowId är satt.

import { useCallback, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Rocket, History, Key, Webhook, Calendar, Loader2 } from 'lucide-react';
import { RunHistoryPanel } from './RunHistoryPanel';
import { ApiKeyManagerPanel } from './ApiKeyManagerPanel';
import { WebhookTriggersPanel } from './WebhookTriggersPanel';
import { ScheduledTriggersPanel } from './ScheduledTriggersPanel';
import { triggerWorkflowRun } from '@/lib/runTrigger';
import { usePlan } from '@/hooks/usePlan';
import { toast } from '@/hooks/use-toast';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workflowId: string | null;
  workflowName: string;
}

export function ProcessMotorDialog({ open, onOpenChange, workflowId, workflowName }: Props) {
  const { limits, tier } = usePlan();
  const [tab, setTab] = useState<'runs' | 'api-keys' | 'webhooks' | 'schedules'>('runs');
  const [triggering, setTriggering] = useState(false);

  const handleTriggerRun = useCallback(async () => {
    if (!workflowId) return;
    setTriggering(true);
    const result = await triggerWorkflowRun({ workflowId, triggerKind: 'manual' });
    setTriggering(false);
    if (result.success) {
      toast({
        title: 'Run startad',
        description: `run_id: ${result.runId?.slice(0, 8)} — status: ${result.status}`,
      });
    } else {
      toast({
        title: 'Kunde inte starta run',
        description:
          result.error === 'enterprise-required'
            ? 'Kräver Enterprise-plan'
            : result.error,
        variant: 'destructive',
      });
    }
  }, [workflowId]);

  if (!workflowId) return null;

  const canUse = limits.canRunProcesses;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl h-[80vh] flex flex-col p-0 gap-0">
        <DialogHeader className="px-5 py-3 border-b flex-shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <Rocket className="w-5 h-5 text-primary" />
            Process-motor: {workflowName || 'Unnamed workflow'}
          </DialogTitle>
          <DialogDescription className="text-xs">
            Trigga runs manuellt, konfigurera API-access, webhooks och
            scheman — allt för detta specifika workflow.
          </DialogDescription>
        </DialogHeader>

        {!canUse && (
          <div className="p-4 bg-amber-50 dark:bg-amber-950/30 border-b border-amber-200 dark:border-amber-800 text-sm">
            <strong>Enterprise-plan krävs.</strong> Din nuvarande plan ({tier}) saknar process-motor-access.
            Flow:en är synliga här men åtgärder blockeras av server-side-gate.
          </div>
        )}

        <Tabs value={tab} onValueChange={(v) => setTab(v as typeof tab)} className="flex flex-col flex-1 min-h-0">
          <div className="px-5 pt-2 pb-0 flex-shrink-0 flex items-center justify-between">
            <TabsList className="h-9">
              <TabsTrigger value="runs" className="text-xs gap-1.5" data-testid="process-tab-runs">
                <History className="w-3.5 h-3.5" />
                Runs
              </TabsTrigger>
              <TabsTrigger value="api-keys" className="text-xs gap-1.5" data-testid="process-tab-api-keys">
                <Key className="w-3.5 h-3.5" />
                API-nycklar
              </TabsTrigger>
              <TabsTrigger value="webhooks" className="text-xs gap-1.5" data-testid="process-tab-webhooks">
                <Webhook className="w-3.5 h-3.5" />
                Webhooks
              </TabsTrigger>
              <TabsTrigger value="schedules" className="text-xs gap-1.5" data-testid="process-tab-schedules">
                <Calendar className="w-3.5 h-3.5" />
                Schemalagt
              </TabsTrigger>
            </TabsList>
            {tab === 'runs' && canUse && (
              <Button
                size="sm"
                onClick={() => void handleTriggerRun()}
                disabled={triggering}
                data-testid="process-trigger-run-btn"
              >
                {triggering ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Rocket className="w-3.5 h-3.5 mr-1" />}
                Kör nu
              </Button>
            )}
          </div>

          <TabsContent value="runs" className="flex-1 min-h-0 mt-0 overflow-hidden">
            <RunHistoryPanel
              workflowId={workflowId}
              onTriggerRun={canUse ? () => void handleTriggerRun() : undefined}
            />
          </TabsContent>
          <TabsContent value="api-keys" className="flex-1 min-h-0 mt-0 overflow-hidden">
            <ApiKeyManagerPanel />
          </TabsContent>
          <TabsContent value="webhooks" className="flex-1 min-h-0 mt-0 overflow-hidden">
            <WebhookTriggersPanel workflowId={workflowId} />
          </TabsContent>
          <TabsContent value="schedules" className="flex-1 min-h-0 mt-0 overflow-hidden">
            <ScheduledTriggersPanel workflowId={workflowId} />
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
